// Comprehensive Results Screen - Apple Health Style
// Enhanced version with 18 Risk Categories + 9 Constitution Types
import { useState } from 'react';
import { useApp } from '../hooks/useApp';
import { SCREENS } from '../config/constants';
import { Container, Text, Card, Button, IconButton } from '../components/ui';
import Icons from '../components/Icons';
import ShareAuditModal from '../components/ShareAuditModal';
import { HealthSummaryCard, ConstitutionCard, RiskCategoryCard } from '../components/cards';
import { RiskCategoryList } from '../components/cards';
import { RiskRadarChart } from '../components/charts';
import { RISK_SYSTEMS, RISK_CATEGORIES } from '../config/riskCategories';
import { CONSTITUTION_TYPES, getTopConstitutions } from '../config/constitutionTypes';

/**
 * Section Tab Component
 */
function SectionTab({ tabs, activeTab, onTabChange }) {
    return (
        <div className="flex gap-2 overflow-x-auto pb-2 mb-4 scrollbar-hide">
            {tabs.map(tab => (
                <button
                    key={tab.id}
                    onClick={() => onTabChange(tab.id)}
                    className={`
            px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap
            transition-all duration-200
            ${activeTab === tab.id
                            ? 'bg-primary text-white'
                            : 'bg-theme-secondary text-theme-secondary hover:bg-theme-tertiary'
                        }
          `}
                >
                    {tab.icon && <span className="mr-1.5">{tab.icon}</span>}
                    {tab.label}
                </button>
            ))}
        </div>
    );
}

/**
 * Diagnosis Detail Card - For Tongue/Face analysis
 */
function DiagnosisCard({ type, data, image }) {
    const config = {
        tongue: {
            icon: <Icons.Tongue />,
            title: 'Thiệt chẩn',
            color: 'primary',
            fields: [
                { key: 'color', label: 'Màu lưỡi' },
                { key: 'shape', label: 'Hình dạng' },
                { key: 'coating', label: 'Rêu lưỡi' },
                { key: 'moisture', label: 'Độ ẩm' }
            ]
        },
        face: {
            icon: <Icons.Face />,
            title: 'Diện chẩn',
            color: 'secondary',
            fields: [
                { key: 'overall', label: 'Sắc mặt' },
                { key: 'eyeArea', label: 'Vùng mắt' },
                { key: 'forehead', label: 'Trán' },
                { key: 'cheeks', label: 'Gò má' }
            ]
        }
    };

    const cfg = config[type];
    if (!cfg || !data) return null;

    return (
        <Card className="mb-4">
            <div className="flex items-center gap-3 sm:gap-4 mb-4">
                <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-${cfg.color}/10 text-${cfg.color} flex items-center justify-center`}>
                    {cfg.icon}
                </div>
                <Text className="font-semibold">{cfg.title}</Text>
            </div>

            {/* Captured Image Preview */}
            {image && (
                <div className="mb-4 rounded-xl overflow-hidden bg-theme-tertiary">
                    <img
                        src={image}
                        alt={cfg.title}
                        className="w-full h-32 object-cover"
                    />
                </div>
            )}

            {/* Analysis Fields */}
            <div className="grid grid-cols-2 gap-3 mb-4">
                {cfg.fields.map(field => (
                    data[field.key] && (
                        <div key={field.key} className="bg-theme-tertiary rounded-xl p-3">
                            <Text variant="small" className="text-theme-tertiary mb-1">{field.label}</Text>
                            <Text className="font-medium">{data[field.key]}</Text>
                        </div>
                    )
                ))}
            </div>

            {/* Explanation */}
            {data.explanation && (
                <div className="bg-theme-secondary p-3 rounded-lg border border-theme">
                    <Text variant="small" className="text-theme-tertiary mb-1 block">💡 Phân tích AI:</Text>
                    <Text variant="caption" className="leading-relaxed">{data.explanation}</Text>
                </div>
            )}
        </Card>
    );
}

/**
 * Recommendations Section with Categories
 */
function RecommendationsSection({ recommendations = {}, riskScores = {} }) {
    const [activeCategory, setActiveCategory] = useState('all');

    // If recommendations is an array (old format), convert to object
    const recsData = Array.isArray(recommendations)
        ? { general: recommendations }
        : recommendations;

    const categories = [
        { id: 'all', label: 'Tất cả', icon: '📋' },
        { id: 'diet', label: 'Dinh dưỡng', icon: '🥗' },
        { id: 'lifestyle', label: 'Sinh hoạt', icon: '🧘' },
        { id: 'acupoints', label: 'Huyệt đạo', icon: '💆' },
        { id: 'herbs', label: 'Thảo dược', icon: '🌿' }
    ];

    const getRecommendationsByCategory = () => {
        let all = [];
        if (activeCategory === 'all') {
            if (recsData.diet) all.push(...recsData.diet.filter(r => r).map(r => typeof r === 'string' ? { text: r, category: 'diet' } : { ...r, category: 'diet' }));
            if (recsData.lifestyle) all.push(...recsData.lifestyle.filter(r => r).map(r => typeof r === 'string' ? { text: r, category: 'lifestyle' } : { ...r, category: 'lifestyle' }));
            if (recsData.acupoints) all.push(...recsData.acupoints.filter(r => r).map(r => typeof r === 'string' ? { text: r, category: 'acupoints' } : { ...r, category: 'acupoints' }));
            if (recsData.herbs) all.push(...recsData.herbs.filter(r => r).map(r => typeof r === 'string' ? { text: r, category: 'herbs' } : { ...r, category: 'herbs' }));

            // Handle general array if provided
            if (recsData.general) {
                all.push(...recsData.general.filter(r => r).map(r => typeof r === 'string' ? { text: r, category: 'general' } : { ...r, category: 'general' }));
            } else if (Array.isArray(recommendations)) {
                all.push(...recommendations.filter(r => r).map(r => ({ text: r, category: 'general' })));
            }
            return all;
        }

        const categoryData = recsData[activeCategory] || [];
        return categoryData
            .filter(r => r && (typeof r === 'string' ? r.trim().length > 0 : r.text))
            .map(r => typeof r === 'string' ? { text: r } : r);
    };

    const filteredRecs = getRecommendationsByCategory().filter(r => r.text && r.text.trim().length > 0);

    return (
        <Card className="mb-4">
            <Text className="font-semibold mb-4">💡 Khuyến nghị cá nhân hóa</Text>

            {/* Category Tabs - only show if we have categorized recommendations */}
            {!Array.isArray(recommendations) && Object.keys(recsData).length > 1 && (
                <SectionTab
                    tabs={categories}
                    activeTab={activeCategory}
                    onTabChange={setActiveCategory}
                />
            )}

            {/* Recommendations List */}
            <div className="space-y-3">
                {filteredRecs.map((rec, idx) => (
                    <div key={idx} className="flex gap-3">
                        <div className="w-6 h-6 rounded-full bg-success/10 text-success flex items-center justify-center shrink-0 mt-0.5">
                            <Icons.Check />
                        </div>
                        <Text className="text-sm leading-relaxed">
                            {rec.text}
                        </Text>
                    </div>
                ))}

                {filteredRecs.length === 0 && (
                    <Text className="text-theme-secondary text-center py-4">
                        Chưa có khuyến nghị cho danh mục này
                    </Text>
                )}
            </div>
        </Card>
    );
}

/**
 * Syndrome Card - Enhanced version
 */
function SyndromeCard({ syndrome }) {
    if (!syndrome) return null;

    return (
        <Card className="mb-4 border-l-4 border-l-primary">
            <Text variant="small" className="text-primary font-bold uppercase tracking-wider mb-2">
                Chứng hậu chính
            </Text>
            <div className="flex items-baseline gap-2 mb-3">
                <Text variant="h3">{syndrome.primary?.name || syndrome.name}</Text>
            </div>

            {/* Yin-Yang & Cold-Heat Tags */}
            {(syndrome.yinYang || syndrome.coldHeat) && (
                <div className="flex gap-2 mb-4 flex-wrap">
                    {syndrome.yinYang && (
                        <span className="px-3 py-1 bg-theme-secondary rounded-lg text-xs font-medium border border-theme">
                            {syndrome.yinYang}
                        </span>
                    )}
                    {syndrome.coldHeat && (
                        <span className="px-3 py-1 bg-theme-secondary rounded-lg text-xs font-medium border border-theme">
                            {syndrome.coldHeat}
                        </span>
                    )}
                </div>
            )}

            {/* Pattern Matching */}
            {syndrome.patterns && syndrome.patterns.length > 0 && (
                <div className="space-y-2">
                    {syndrome.patterns.map((pattern, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-theme-secondary rounded-lg">
                            <div className="flex items-center gap-3">
                                <div className="w-2 h-2 rounded-full bg-primary" />
                                <Text className="font-medium text-sm">{pattern.name}</Text>
                            </div>
                            <Text className="text-success font-bold text-sm">{pattern.match}%</Text>
                        </div>
                    ))}
                </div>
            )}

            {/* Description */}
            {syndrome.description && (
                <Text variant="caption" className="mt-3 text-theme-secondary leading-relaxed">
                    {syndrome.description}
                </Text>
            )}
        </Card>
    );
}

/**
 * Main Results Screen Component
 */
function ResultsScreen() {
    const {
        setCurrentScreen,
        analysisResults,
        questionnaireAnswers,
        capturedImages,
        rawAIResponse
    } = useApp();

    const [showShareModal, setShowShareModal] = useState(false);
    const [activeSection, setActiveSection] = useState('overview');

    if (!analysisResults) return null;

    // Destructure analysis results with defaults
    const {
        overallScore = 75,
        tongue = {},
        face = {},
        syndrome = {},
        recommendations = [],
        // New fields for comprehensive report
        riskCategories = {},
        constitution = {},
        systemScores = {},
        meridians = []
    } = analysisResults;

    // Prepare constitution data for cards
    const constitutionScores = constitution?.scores || {};
    const topConstitutions = getTopConstitutions(constitutionScores, 3);

    // Sections for navigation
    const sections = [
        { id: 'overview', label: 'Tổng quan', icon: '📊' },
        { id: 'risks', label: 'Rủi ro', icon: '⚠️' },
        { id: 'constitution', label: 'Thể chất', icon: '🧬' },
        { id: 'diagnosis', label: 'Chẩn đoán', icon: '🔬' },
        { id: 'recommendations', label: 'Khuyến nghị', icon: '💡' }
    ];

    // Calculate primary constitution for summary
    const primaryConstitution = topConstitutions[0] || null;
    const secondaryConstitutions = topConstitutions.slice(1);

    return (
        <Container className="min-h-screen bg-theme-bg pb-24" safe>
            {/* Header */}
            <div className="header-fullwidth border-b border-theme">
                <div className="header-content">
                    <IconButton onClick={() => setCurrentScreen(SCREENS.HOME)}>
                        <Icons.Back />
                    </IconButton>
                    <Text className="font-semibold sm:text-lg">Báo cáo Sức khỏe</Text>
                    <IconButton onClick={() => setShowShareModal(true)}>
                        <Icons.Share />
                    </IconButton>
                </div>
            </div>

            <div className="main-content pt-4 sm:pt-6">
                {/* Section Navigation */}
                <SectionTab
                    tabs={sections}
                    activeTab={activeSection}
                    onTabChange={setActiveSection}
                />

                {/* ===== OVERVIEW SECTION ===== */}
                {activeSection === 'overview' && (
                    <>
                        {/* Health Summary Card */}
                        <div className="mb-6">
                            <HealthSummaryCard
                                overallScore={overallScore}
                                systemScores={systemScores}
                                primaryConstitution={primaryConstitution}
                                secondaryConstitutions={secondaryConstitutions}
                                timestamp={analysisResults._timestamp}
                                isMock={analysisResults._isMock}
                                analysisMethod={analysisResults._model || 'AI'}
                            />
                        </div>

                        {/* Syndrome Quick View */}
                        <SyndromeCard syndrome={syndrome} />

                        {/* Quick Risk Summary - Show top 3 risks */}
                        {Object.keys(riskCategories).length > 0 && (
                            <Card className="mb-4">
                                <div className="flex items-center justify-between mb-3">
                                    <Text className="font-semibold">⚠️ Rủi ro cần chú ý</Text>
                                    <button
                                        onClick={() => setActiveSection('risks')}
                                        className="text-sm text-primary"
                                    >
                                        Xem tất cả →
                                    </button>
                                </div>
                                <RiskCategoryList
                                    riskScores={riskCategories}
                                    maxVisible={3}
                                    showTitle={false}
                                />
                            </Card>
                        )}
                    </>
                )}

                {/* ===== RISKS SECTION ===== */}
                {activeSection === 'risks' && (
                    <>
                        {/* Radar Chart */}
                        {Object.keys(systemScores).length > 0 && (
                            <Card className="mb-4">
                                <Text className="font-semibold mb-4">📊 Tổng quan 5 Hệ thống</Text>
                                <div className="flex justify-center">
                                    <RiskRadarChart
                                        data={Object.entries(RISK_SYSTEMS).map(([key, system]) => ({
                                            category: system.name,
                                            value: systemScores[system.id] || 0,
                                            color: system.color
                                        }))}
                                        size={280}
                                    />
                                </div>
                            </Card>
                        )}

                        {/* Risk Categories List */}
                        <RiskCategoryList
                            riskScores={riskCategories}
                            maxVisible={5}
                        />
                    </>
                )}

                {/* ===== CONSTITUTION SECTION ===== */}
                {activeSection === 'constitution' && (
                    <>
                        <ConstitutionCard constitution={constitution} />

                        {/* Additional constitution info if available */}
                        {constitution?.details?.dietary && (
                            <Card className="mb-4">
                                <Text className="font-semibold mb-3">🥗 Chế độ ăn theo thể chất</Text>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Text variant="small" className="text-success mb-2">✅ Nên ăn:</Text>
                                        <div className="flex flex-wrap gap-1.5">
                                            {constitution.details.dietary.recommended?.slice(0, 6).map((food, idx) => (
                                                <span key={idx} className="text-xs px-2 py-1 bg-success/10 text-success rounded-full">
                                                    {food}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <Text variant="small" className="text-error mb-2">❌ Nên kiêng:</Text>
                                        <div className="flex flex-wrap gap-1.5">
                                            {constitution.details.dietary.avoid?.slice(0, 6).map((food, idx) => (
                                                <span key={idx} className="text-xs px-2 py-1 bg-error/10 text-error rounded-full">
                                                    {food}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        )}
                    </>
                )}

                {/* ===== DIAGNOSIS SECTION ===== */}
                {activeSection === 'diagnosis' && (
                    <>
                        <DiagnosisCard
                            type="tongue"
                            data={tongue}
                            image={capturedImages?.tongue}
                        />
                        <DiagnosisCard
                            type="face"
                            data={face}
                            image={capturedImages?.face}
                        />
                    </>
                )}

                {/* ===== RECOMMENDATIONS SECTION ===== */}
                {activeSection === 'recommendations' && (
                    <RecommendationsSection
                        recommendations={recommendations}
                        riskScores={riskCategories}
                    />
                )}

                {/* Actions */}
                <div className="flex gap-3 pb-6 mt-6">
                    <Button variant="secondary" onClick={() => setCurrentScreen(SCREENS.HOME)} className="flex-1">
                        Trang chủ
                    </Button>
                    <Button onClick={() => setCurrentScreen(SCREENS.HISTORY)} className="flex-1">
                        Xem lịch sử
                    </Button>
                </div>
            </div>

            {/* Share/Audit Modal */}
            <ShareAuditModal
                isOpen={showShareModal}
                onClose={() => setShowShareModal(false)}
                analysisResults={analysisResults}
                rawAIResponse={rawAIResponse || analysisResults?._rawResponse}
                questionnaireAnswers={questionnaireAnswers}
                capturedImages={capturedImages}
                modelUsed={analysisResults?._model}
            />
        </Container>
    );
}

export default ResultsScreen;
